import { getRouteApi, Link } from "@tanstack/react-router";
import { useInstallation } from "@/installation/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle, ChevronLeft, ChevronRight, Clock, ExternalLink, MessageSquare, RefreshCw, TrendingUp, Zap } from "lucide-react";
import { Suspense, useState } from "react";
import { IssuesCard } from "@/opportunity/IssuesCard";
import {
  getShortEquipmentDetails,
  InstallationData,
  installationHealthStatusBackgroundClass,
  installationHealthStatusTextClassSeverityMessage,
  isRefrigerant,
  isCapture,
  numDataPointsPerMinutePerAsset
} from "@/installation/installation";
import { DateRangeContext, useDateRange } from "@/dateRanges/DateRangeContext";
import { CalendarDateRangePicker } from "@/components/date-range-picker";
import { DatumCardQuery } from "@/components/datum-card-query";
import { ChartOptionsContextProvider } from "@/chartOptions/ChartOptionsContext";
import { ApiPath } from "@/api/path";
import { DateRange, LAST_24_HOURS, LAST_7_DAYS } from "@/dateRanges/dateRanges";
import { AUTO, ONE_HOUR, WindowSize } from "@/dateRanges/windowSizes";
import { SimpleDateRangeSelector } from "@/refrigerant/useSimpleDateRangeOption";
import { AggregateNormalizeSelector } from "@/installation/AggregateNormalizeContext";
import ChartOptionsSelector from "@/chartOptions/chartOptionsSelector";
import AssetsEnergyConsumptionChartCard from "@/refrigerant/AssetsEnergyConsumptionChartCard";
import DraggableCompressorTempCard from "@/components/draggable-compressor-temp-card";
import DraggableRawTempCard from "@/components/draggable-raw-temp-card";
import { CollapsibleCard } from "@/opportunity/opportunity_detail_page/CollapsibleCard";
import { useOpportunities } from "@/opportunity/api";
import { IssueFeedback, OpportunityTableFilters } from "@/opportunity/opportunity";
import { FeedbackTimelineEntry } from "@/opportunity/IssueFeedbackSection";
import { Spinner } from "@/components/ui/spinner";

const AssetHealthOverview = () => {
  const route = getRouteApi("/_authenticated/organization/$organizationId/_non_installer/locations/$locationId/assets/$assetId/health")
  const { assetId, locationId, organizationId } = route.useParams();
  const { data: selectedAsset } = useInstallation(assetId);

  const filters = {
    installation_ids: [assetId],
  };

  return (
    <div className="space-y-6">
      {/* Asset Header */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div
              className={`h-4 w-4 rounded-full ${installationHealthStatusBackgroundClass[selectedAsset.health_status]}`}
            />
            <div>
              <h1 className="text-2xl font-bold">{getShortEquipmentDetails(selectedAsset.asset)}</h1>
              <p className="text-muted-foreground">
                Asset ID: {assetId} • {selectedAsset.sublocation}
              </p>
            </div>
          </div>
          <div className="text-right">
            <Badge 
              className={`${installationHealthStatusBackgroundClass[selectedAsset.health_status]} text-white`}
            >
              {selectedAsset.health_status.toUpperCase()}
            </Badge>
            <p className="text-sm text-muted-foreground mt-1">
              {installationHealthStatusTextClassSeverityMessage[selectedAsset.health_status]}
            </p>
          </div>
        </div>
      </Card>

      {/* Health Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Current Status</CardTitle>
            <div className={`h-3 w-3 rounded-full ${installationHealthStatusBackgroundClass[selectedAsset.health_status]}`} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold capitalize">{selectedAsset.health_status}</div>
            <p className="text-xs text-muted-foreground">
              {installationHealthStatusTextClassSeverityMessage[selectedAsset.health_status]}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Equipment Type</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{selectedAsset.asset.asset_type.replace('_', ' ').toUpperCase()}</div>
            <p className="text-xs text-muted-foreground">
              {selectedAsset.asset.make} {selectedAsset.asset.model}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Last Updated</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">Live</div>
            <p className="text-xs text-muted-foreground">
              Real-time monitoring active
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Issues Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Current Issues & Recommendations
          </CardTitle>
          <CardDescription>
            Issues detected for this asset and recommended actions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <IssuesCard 
            filters={filters} 
            numDataPointsPerMinute={numDataPointsPerMinutePerAsset(selectedAsset)}
          />
        </CardContent>
      </Card>

      {/* Compressor Temperature Analysis */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <DraggableCompressorTempCard
          installationId={selectedAsset.id.toString()}
          locationName={selectedAsset.location.name}
          sensorKitSerialNumber={selectedAsset.sensor_kit?.serial_number || 'Unknown'}
          maxDurationMinutes={2}
        />
        
        <DraggableRawTempCard
          installationId={selectedAsset.id.toString()}
          organizationId={selectedAsset.location.organization_id.toString()}
          locationName={selectedAsset.location.name}
          sensorKitSerialNumber={selectedAsset.sensor_kit?.serial_number || 'Unknown'}
          maxDurationMinutes={2}
        />
      </div>

      {/* Performance Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Performance Overview
          </CardTitle>
          <CardDescription>
            Key performance metrics and energy consumption data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <AssetPerformanceCharts selectedAsset={selectedAsset} />
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>
            Common actions for this asset
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <Button variant="outline" asChild>
              <Link
                to="/organization/$organizationId/locations/$locationId/assets/$assetId"
                params={{
                  organizationId: organizationId,
                  locationId: locationId,
                  assetId
                }}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                View Detailed Analytics
              </Link>
            </Button>
            <Button variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh Data
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Feedback Section */}
      <Suspense fallback={<Spinner />}>
        <AssetFeedbackSection filters={filters} />
      </Suspense>
    </div>
  );
};

const PAGE_SIZE = 3;

const AssetFeedbackSection = ({ filters }: { filters: OpportunityTableFilters }) => {
  const [page, setPage] = useState(0);

  const { data: { results: activeOpportunities } } = useOpportunities({
    filters: { ...filters, requires_action: true },
    pageSize: 100,
  });
  const { data: { results: resolvedOpportunities } } = useOpportunities({
    filters: { ...filters, requires_action: false },
    pageSize: 100,
  });

  const allFeedback: (IssueFeedback & { opportunityName: string })[] = [
    ...activeOpportunities,
    ...resolvedOpportunities,
  ].flatMap((opp) =>
    (opp.feedback ?? []).map((fb) => ({ ...fb, opportunityName: opp.name }))
  );

  const sorted = [...allFeedback].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const displayed = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  if (sorted.length === 0) return null;

  return (
    <CollapsibleCard
      title={
        <span className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Feedback
        </span>
      }
      defaultOpen={false}
    >
      <div className="flex flex-col divide-y divide-border/50">
        {displayed.map((entry) => (
          <div key={entry.id}>
            <p className="text-xs text-muted-foreground mt-1">
              Issue: <span className="font-medium text-foreground">{entry.opportunityName}</span>
            </p>
            <FeedbackTimelineEntry entry={entry} />
          </div>
        ))}
      </div>
      {sorted.length > PAGE_SIZE && (
        <div className="flex items-center justify-between pt-2 border-t mt-2">
          <span className="text-xs text-muted-foreground">
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, sorted.length)} of {sorted.length}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </CollapsibleCard>
  );
};

const AssetPerformanceCharts = ({ selectedAsset }: {
  selectedAsset: InstallationData,
}) => {
  const [energyDateRange, setEnergyDateRange] = useState<DateRange>(LAST_24_HOURS);
  const [energyWindowSize, setEnergyWindowSize] = useState<WindowSize>(ONE_HOUR);

  if (isRefrigerant(selectedAsset)) {
    return (
      <DateRangeContext.Provider value={{
        dateRange: energyDateRange,
        setDateRange: setEnergyDateRange,
        windowSize: energyWindowSize,
        setWindowSize: setEnergyWindowSize,
      }}>
        <ChartOptionsContextProvider location={selectedAsset.location}>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold text-lg">Energy Consumption</h3>
              <div className="flex items-center gap-2">
                <ChartOptionsSelector/>
                <AggregateNormalizeSelector/>
                <SimpleDateRangeSelector/>
              </div>
            </div>
            <AssetsEnergyConsumptionChartCard
              assets={[selectedAsset]}
            />
          </div>
        </ChartOptionsContextProvider>
      </DateRangeContext.Provider>
    );
  }

  if (isCapture(selectedAsset)) {
    const { dateRange, windowSize, refreshInterval } = useDateRange({
      overrideDefaultDateRange: LAST_7_DAYS,
      overrideDefaultWindowSize: AUTO,
    });

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="font-semibold text-lg">CO₂ Capture Performance</h3>
          <div className="flex items-center gap-2">
            <ChartOptionsSelector/>
            <CalendarDateRangePicker/>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <DatumCardQuery
            title="CO₂ Captured"
            apiPath={ApiPath.INSTALLATION_TOTAL_KG_CAPTURED}
            suffixFn={(res) => res?.suffix}
            text="Total amount removed from the building's air"
            accessorFn={(res: any) => res}
            query={{
              installation_id: [selectedAsset.id],
              windowSize,
              dateRange
            }}
            refreshInterval={refreshInterval}
            hideIfNoData={true}
            hideIfZero={true}
          />
          <DatumCardQuery
            title="Maximum Rate"
            apiPath={ApiPath.INSTALLATION_TREE_EQUIV_AND_MAX_RATE}
            suffix={"g/hr"}
            text="Maximum capture rate"
            accessorFn={(res: any) =>
              res?.datums.find((item: any) => item.name == "max_rate")
            }
            query={{
              installation_id: [selectedAsset.id],
              windowSize,
              dateRange
            }}
            refreshInterval={refreshInterval}
            hideIfNoData={true}
            hideIfZero={true}
          />
          <DatumCardQuery
            title="Tree Equivalent"
            apiPath={ApiPath.INSTALLATION_TREE_EQUIV_AND_MAX_RATE}
            suffix={"trees"}
            text="Approximate number of trees needed"
            accessorFn={(res: any) =>
              res?.datums.find((item: any) => item.name == "tree_equiv")
            }
            query={{
              installation_id: [selectedAsset.id],
              windowSize,
              dateRange
            }}
            refreshInterval={refreshInterval}
            hideIfNoData={true}
            hideIfZero={true}
          />
        </div>
      </div>
    );
  }

  // Default for combustion assets
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-lg">Performance Metrics</h3>
        <div className="flex items-center gap-2">
          <ChartOptionsSelector/>
          <CalendarDateRangePicker/>
        </div>
      </div>
      <div className="text-center py-8 text-muted-foreground">
        <CheckCircle className="h-12 w-12 mx-auto mb-4" />
        <p>Performance monitoring is active for this asset</p>
        <p className="text-sm">Detailed metrics are available in the main analytics view</p>
      </div>
    </div>
  );
};

const AssetHealthPage = () => {
  return <AssetHealthOverview />;
};

export default AssetHealthPage;
